import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Text from 'core/elements/Text'
import { rebuildVirtualMachine } from './actions'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import Theme from 'core/themes/model'
import { makeStyles } from '@material-ui/styles'
import Info from 'core/components/validatedForm/Info'
import { getCurrentUser } from 'openstack/helpers'
import { hasOtherUserVm } from './helpers'
import { getSingleVolume } from '../storage/volumes/actions'
import PicklistField from 'core/components/validatedForm/DropdownField'
import ImagePicklist from './ImagePicklist'
import useParams from 'core/hooks/useParams'

// Todo: Check into how to rebuild vms that used bootable volume
export default function RebuildVirtualMachineDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const { params, getParamsUpdater } = useParams<Record<string, any>>({
    imageId: null,
  })

  const ipAddresses = useMemo(() => {
    const networkKeys = Object.keys(vm?.addresses)
    const ips = networkKeys.map((key) => vm?.addresses?.[key].map((iface) => iface.addr)).flat()
    return ips
  }, [vm])

  const { update: rebuildVm, updating, error } = useUpdateAction(rebuildVirtualMachine)
  const [bootableImageDetails, setBootableImageDetails] = useState<{ id: string; name: string }>(
    null,
  )
  const [loadingVolumes, setLoadingVolumes] = useState(true)

  const getVolumes = useCallback(async () => {
    if (!vm?.volumeIds?.length) {
      setLoadingVolumes(false)
      return
    }
    try {
      setLoadingVolumes(true)
      const promises = vm?.volumeIds?.map((id) => getSingleVolume(id))
      const responses = await Promise.allSettled(promises)
      const vols = responses
        .filter((response) => response.status === 'fulfilled')
        .map((response: any) => response.value)
      const bootableVolume = vols?.find((vol) => vol.bootable === 'true') || null
      setBootableImageDetails({
        id: bootableVolume?.volume_image_metadata?.image_id,
        name: bootableVolume?.volume_image_metadata?.image_name,
      })
    } catch (e) {
      console.log(e)
    } finally {
      setLoadingVolumes(false)
    }
  }, [vm?.volumeIds])

  useEffect(() => {
    if (!(vm?.imageDetails?.name || vm?.imageDetails?.id)) {
      getVolumes()
    }
  }, [vm, getVolumes])

  const submitForm = useCallback(async () => {
    const body = {
      rebuild: {
        imageRef: vm?.imageDetails?.id || bootableImageDetails?.id || params.imageId,
      },
    }
    const { success } = await rebuildVm({
      id: vm.id,
      body,
    })
    if (success) onClose(true)
  }, [vm, bootableImageDetails, params, rebuildVm, onClose])

  const { role: userRole, userId } = getCurrentUser()

  const filteredImages = useCallback((images) => {
    return images?.filter((image) => image.status === 'active' && image?.imageType === 'image')
  }, [])

  return (
    <ModalForm
      open
      panel="dialog"
      title={`Rebuild VM`}
      onSubmit={submitForm}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Rebuild VM`}
      maxWidth={800}
      loading={!vm?.imageDetails && loadingVolumes}
    >
      <div className={classes.body}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">
          You are about to rebuild the VM <b>{vm?.name || vm?.id}</b>
        </Text>
        <Text variant="body2">
          Rebuilding a VM will redeploy the selected VM based on the VM's image and existing flavor,
          network, and storage configuration. This operation will recreate the root disk of the VM.
          For a volume-backed VM, this operation will keep the contents of the volume.
        </Text>
        <Text variant="body2">
          <b>IMPORTANT:</b> This will delete all the data on the VM and replace it with the
          specified image. Only the VM ID, flavor and IP address (fixed and floating) will remain
          the same.
        </Text>
        <div className={classes.properties}>
          {vm?.imageDetails?.name ||
          vm?.imageDetails?.id ||
          bootableImageDetails?.name ||
          bootableImageDetails?.id ? (
            <>
              <Text variant="caption1">Image:</Text>
              <Text variant="body2">
                {vm?.imageDetails?.name ||
                  vm?.imageDetails?.id ||
                  bootableImageDetails?.name ||
                  bootableImageDetails?.id ||
                  'unknown'}
              </Text>
            </>
          ) : (
            <PicklistField
              DropdownComponent={ImagePicklist}
              id="image"
              label="Select Image"
              filterFn={(images) => filteredImages(images)}
              onChange={getParamsUpdater('imageId')}
              value={params.imageId}
              className={classes.imagePicklist}
              required
            />
          )}

          <Text variant="caption1">Flavor:</Text>
          <Text variant="body2">
            {vm?.flavor?.original_name || 'unknown'}{' '}
            {vm?.flavor
              ? `(${vm?.flavor?.vcpus} vCPUs, ${vm?.flavor?.ram} RAM (MB), ${vm?.flavor?.disk} Disk (GB))`
              : ''}
          </Text>
          <Text variant="caption1">IP Addresses:</Text>
          <Text variant="body2">{ipAddresses.join(', ')}</Text>
        </div>
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  body: {
    display: 'grid',
    gap: 12,
  },
  properties: {
    display: 'grid',
    gridTemplateColumns: 'auto',
    gap: '8px 16px',
    '& .dropdown-container > label, & .dropdown-container > div': {
      marginLeft: 0,
    },
  },
}))
